import { useQuery } from '@apollo/client';
import { CompaniesNav } from '@clearkit/icons';
import classNames from 'classnames';
import { CKCheckboxNext, CKLayoutHorizontal } from 'clearkit';
import { useEffect, useState } from 'react';

import { ItemCount } from '~/components/ListItem/ItemCount';
import { PageLayout } from '~/components/PageLayout';
import {
  MAX_COUNT_HIDE,
  MAX_COUNT_HIDE_LABEL,
} from '~/components/prospector/useProspector/constants';
import BulkSelectedCompaniesActions from '~/components/tools/BulkSelectedCompaniesActions';
import { trackEvent } from '~/lib/tracking';

import { Search } from '../hooks/useTargetMarket';
import TargetMarketCompaniesQuery from '../queries/TargetMarketCompanies.graphql';
import { SAMPLE_COMPANIES } from '../Sidebar/logic';
import { TargetMarketsCompany } from '../types';
import { CompanyItem } from './CompanyItem';
import { EmptyList } from './EmptyList';

const PLACEHOLDER_COMPANY: TargetMarketsCompany = {
  id: '',
  domain: '',
  encryptedDomain: '',
  name: 'Placeholder',
  country: 'Placeholder',
  industry: 'Placeholder',
  employeesRange: 'Placeholder',
  linkedin: 'Placeholder',
  twitter: 'Placeholder',
  crunchbase: 'Placeholder',
};

const CompaniesList = ({ search }: { search: Search | null }) => {
  const [previousTotalCompanies, setPreviousTotalCompanies] = useState(0);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  const { data } = useQuery(TargetMarketCompaniesQuery, {
    skip: !search,
    variables: {
      country: search?.country || [],
      description: search?.description || [],
      employees: search?.employees || [],
      industry: search?.industry || [],
      raised: search?.raised || [],
      tag: search?.tag || [],
      tech: search?.tech || [],
      type: search?.type || [],
    },
  }) as {
    data?: {
      targetMarketCompanies?: {
        total: number;
        companies: TargetMarketsCompany[];
      };
    };
  };
  const showPlaceholder = search === null;

  useEffect(() => {
    if (data) {
      setPreviousTotalCompanies(data?.targetMarketCompanies?.total || 0);
    }
  }, [data]);

  useEffect(() => {
    setIsSelectAll(
      !!companies.length && companies.length === selectedCompanies.length,
    );
  }, [selectedCompanies]);

  const loading = data === undefined;
  const total = data?.targetMarketCompanies?.total || 0;
  const selectedCompaniesCount = selectedCompanies.length;

  const companies: TargetMarketsCompany[] = showPlaceholder
    ? SAMPLE_COMPANIES
    : loading
    ? Array(20).fill(PLACEHOLDER_COMPANY)
    : data?.targetMarketCompanies?.companies || [];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setIsSelectAll(true);
      const domains = companies.map((company) => company.domain);
      setSelectedCompanies(companies ? domains : []);

      trackEvent('target_market_all_companies_selected', {
        domains,
      });
    } else {
      setIsSelectAll(false);
      setSelectedCompanies([]);

      trackEvent('target_market_selected_companies_cleared');
    }
  };

  const handleSelect = (domain: string, selectCompany: boolean) => {
    if (selectCompany) {
      setSelectedCompanies((prev) => [...prev, domain]);

      trackEvent('target_market_company_selected', { domain });
    } else {
      setSelectedCompanies((prev) => prev.filter((item) => item !== domain));

      trackEvent('target_market_company_deselected', {
        domain,
      });
    }
  };

  return (
    <PageLayout className="@container/list">
      <PageLayout.HeaderWrapper className="py-3 pb-3 xl:py-3">
        <CKLayoutHorizontal>
          <ItemCount
            end={total}
            hideCountAbove={MAX_COUNT_HIDE}
            hideCountLabel={MAX_COUNT_HIDE_LABEL}
            label={total === 1 ? 'company' : 'companies'}
            start={previousTotalCompanies}
          >
            <CompaniesNav height={18} width={18} />
          </ItemCount>
        </CKLayoutHorizontal>
      </PageLayout.HeaderWrapper>
      <PageLayout.Body
        className={classNames('relative', {
          'overflow-hidden': showPlaceholder,
        })}
      >
        <PageLayout.Content>
          <EmptyList isVisible={showPlaceholder} />

          {!loading && companies.length === 0 ? (
            <div className="px-6 py-40 text-center bg-white">
              <p className="text-center">
                No companies found, please try
                <br />
                updating your filters.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {companies.map((company, index) => (
                <CompanyItem
                  company={company}
                  isSelected={selectedCompanies.includes(company.domain)}
                  key={`${company.domain}${index.toString()}`}
                  loading={showPlaceholder ? false : loading}
                  number={index + 1}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          )}
        </PageLayout.Content>
      </PageLayout.Body>
      <PageLayout.Footer>
        <CKLayoutHorizontal>
          <CKLayoutHorizontal>
            <CKCheckboxNext
              checked={isSelectAll}
              isDisabled={loading || companies.length === 0}
              isIndeterminate={
                selectedCompaniesCount > 0 &&
                selectedCompaniesCount !== companies.length
              }
              key={`select-all-${selectedCompaniesCount}`}
              onChange={handleSelectAll}
            >
              Select All
            </CKCheckboxNext>
            <ItemCount
              end={selectedCompaniesCount}
              label={`${
                selectedCompaniesCount === 1 ? 'company' : 'companies'
              } selected`}
            >
              <CompaniesNav height={18} width={18} />
            </ItemCount>
          </CKLayoutHorizontal>
          <BulkSelectedCompaniesActions
            handleClear={() => {
              setSelectedCompanies([]);
              trackEvent('target_market_selected_companies_cleared');
            }}
            isSample={false}
            selected={selectedCompanies}
            type="target_markets"
          />
        </CKLayoutHorizontal>
      </PageLayout.Footer>
    </PageLayout>
  );
};

export default CompaniesList;
